import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Factory, CreditCard, AlertTriangle, TrendingUp, TrendingDown, Clock,
  Users, ArrowUpRight, Package, Truck, Target, Award, ShieldAlert
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from 'recharts';

const KPICard = ({ title, value, subtitle, icon: Icon, color = "text-amber-500", testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 nexus-kpi-card" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color.replace('text-', 'bg-').replace('500', '500/10')}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{value}</p>
    <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-medium">{title}</p>
    {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
  </div>
);

const STATUS_COLORS = [
  'hsl(38, 92%, 50%)', 'hsl(198, 93%, 60%)', 'hsl(160, 84%, 39%)',
  'hsl(350, 89%, 60%)', 'hsl(0, 0%, 55%)', 'hsl(262, 83%, 58%)',
  'hsl(30, 80%, 55%)', 'hsl(180, 60%, 45%)',
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/dashboard/stats');
        setStats(data);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 h-32 nexus-shimmer" />
          ))}
        </div>
      </div>
    );
  }

  const statusData = stats?.status_counts
    ? Object.entries(stats.status_counts).map(([key, value]) => ({ name: key, value }))
    : [];

  const summaryData = [
    { name: 'CA', value: stats?.total_revenue || 0 },
    { name: 'Couts', value: stats?.total_revenue ? stats.total_revenue - (stats?.total_margin || 0) : 0 },
    { name: 'Marge', value: stats?.total_margin || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Tableau de bord
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Vue d'ensemble de l'activité ProdLink</p>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testId="kpi-orders" title="Commandes en cours" value={stats?.active_orders || 0} icon={Package} color="text-sky-400" />
        <KPICard testId="kpi-revenue" title="CA en cours" value={formatCurrency(stats?.total_revenue)} icon={TrendingUp} color="text-emerald-400" />
        <KPICard testId="kpi-margin" title="Marge estimee" value={`${stats?.margin_pct || 0}%`} subtitle={formatCurrency(stats?.total_margin)} icon={ArrowUpRight} color="text-amber-500" />
        <KPICard testId="kpi-urgent" title="Commandes urgentes" value={stats?.urgent_count || 0} icon={AlertTriangle} color="text-rose-400" />
        <KPICard testId="kpi-payments-30d" title="Paiements à venir (30j)" value={stats?.payments_due_30d_count || 0} subtitle={formatCurrency(stats?.payments_due_30d_amount)} icon={CreditCard} color="text-sky-400" />
        <KPICard testId="kpi-late" title="Paiements en retard" value={stats?.late_payments || 0} icon={CreditCard} color="text-rose-400" />
        <KPICard testId="kpi-subs" title="Sous-traitants actifs" value={stats?.active_subcontractors || 0} icon={Users} color="text-violet-400" />
        <KPICard testId="kpi-blocked" title="Projets bloques" value={stats?.blocked_count || 0} icon={Clock} color="text-amber-500" />
      </div>

      {/* Charts & Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm col-span-1 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium">Rentabilité</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summaryData}>
                  <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '2px' }}
                    labelStyle={{ color: '#a1a1aa' }}
                    itemStyle={{ color: '#f4f4f5' }}
                    formatter={(v) => formatCurrency(v)}
                  />
                  <Bar dataKey="value" fill="hsl(38, 92%, 50%)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium">Répartition statuts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={40} outerRadius={70}
                      stroke="none"
                    >
                      {statusData.map((_, index) => (
                        <Cell key={index} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '2px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                      itemStyle={{ color: '#f4f4f5' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Aucune donnee</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* === BUSINESS INTELLIGENCE === */}
      <div className="space-y-4" data-testid="bi-section">
        <div className="flex items-center justify-between">
          <h2 className="text-sm uppercase tracking-wider text-zinc-500 font-medium">Pilotage business</h2>
          <span className="text-[10px] text-zinc-600">12 derniers mois · RFQ 90j</span>
        </div>

        {/* Mini-KPIs row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="bi-yoy">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">CA mois vs N-1</p>
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.revenue_current_month)}</p>
              {stats?.yoy_delta_pct !== undefined && stats?.yoy_delta_pct !== 0 && (
                <span className={`text-xs font-mono flex items-center gap-0.5 ${stats.yoy_delta_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stats.yoy_delta_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {stats.yoy_delta_pct > 0 ? '+' : ''}{stats.yoy_delta_pct}%
                </span>
              )}
            </div>
            <p className="text-[10px] text-zinc-600 mt-0.5">N-1 : {formatCurrency(stats?.revenue_prev_year_same_month)}</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="bi-conversion">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Taux conversion RFQ</p>
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{stats?.rfq_conversion_rate || 0}%</p>
              <Target className="w-4 h-4 text-violet-400" />
            </div>
            <p className="text-[10px] text-zinc-600 mt-0.5">{stats?.rfq_won_90d || 0}/{stats?.rfq_total_90d || 0} demandes (90j)</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="bi-avg-margin">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Marge moyenne</p>
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold text-emerald-400" style={{fontFamily:'Cabinet Grotesk'}}>{stats?.avg_margin_pct || 0}%</p>
              <ArrowUpRight className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="text-[10px] text-zinc-600 mt-0.5">{formatCurrency(stats?.avg_margin_amount)} par projet</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="bi-quality">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Taux problème qualité</p>
            <div className="flex items-baseline gap-2">
              <p className={`text-lg font-bold ${(stats?.quality_issue_rate || 0) > 10 ? 'text-rose-400' : 'text-zinc-50'}`} style={{fontFamily:'Cabinet Grotesk'}}>{stats?.quality_issue_rate || 0}%</p>
              <ShieldAlert className={`w-4 h-4 ${(stats?.quality_issue_rate || 0) > 10 ? 'text-rose-400' : 'text-zinc-500'}`} />
            </div>
            <p className="text-[10px] text-zinc-600 mt-0.5">{stats?.quality_issue_count || 0} signalements · {stats?.quality_unresolved || 0} ouverts</p>
          </div>
        </div>

        {/* Revenue evolution chart */}
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Évolution CA (12 mois)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56" data-testid="revenue-evolution-chart">
              {stats?.revenue_evolution?.some(m => m.revenue > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.revenue_evolution}>
                    <CartesianGrid stroke="#27272a" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={m => m.slice(5)} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '2px' }}
                      labelStyle={{ color: '#a1a1aa' }}
                      itemStyle={{ color: '#f4f4f5' }}
                      formatter={(v) => formatCurrency(v)}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="hsl(38, 92%, 50%)" strokeWidth={2} dot={{ r: 3, fill: 'hsl(38, 92%, 50%)' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Pas de données suffisantes</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top subcontractors + Cashflow warning */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm lg:col-span-2" data-testid="top-subs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium flex items-center gap-2">
                <Award className="w-4 h-4" /> Top 5 sous-traitants (par CA)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_subcontractors?.length > 0 ? (
                <div className="space-y-2">
                  {stats.top_subcontractors.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0" data-testid={`top-sub-${i}`}>
                      <div className="w-7 h-7 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-bold text-amber-400 shrink-0">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-200 truncate">{s.name}</p>
                        <p className="text-[11px] text-zinc-500 truncate">{s.specialty || '—'} · {s.projects} projet(s) · {s.completed} terminé(s)</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-emerald-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(s.revenue)}</p>
                        <p className="text-[10px] text-zinc-500">
                          Marge {formatCurrency(s.margin)}
                          {s.quality_score !== null && s.quality_score !== undefined && (
                            <span className={`ml-2 ${s.quality_score >= 80 ? 'text-emerald-400' : s.quality_score >= 60 ? 'text-amber-400' : 'text-rose-400'}`}>
                              Q:{s.quality_score}
                            </span>
                          )}
                          {s.quality_issues > 0 && <span className="text-rose-400 ml-1">· {s.quality_issues}⚠</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 py-4 text-center">Aucune donnée sous-traitant</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="cashflow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Trésorerie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-3">
                <p className="text-[10px] text-rose-300/70 uppercase tracking-wider mb-1">En retard</p>
                <p className="text-lg font-bold text-rose-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.late_payments_amount)}</p>
                <p className="text-[11px] text-rose-300/60">{stats?.late_payments || 0} échéance(s)</p>
              </div>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-3">
                <p className="text-[10px] text-amber-300/70 uppercase tracking-wider mb-1">À recevoir (30j)</p>
                <p className="text-lg font-bold text-amber-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.payments_due_30d_amount)}</p>
                <p className="text-[11px] text-amber-300/60">{stats?.payments_due_30d_count || 0} échéance(s)</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upcoming deliveries */}
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium flex items-center gap-2">
              <Truck className="w-4 h-4" /> Prochaines livraisons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_deliveries?.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_deliveries.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-zinc-200">{p.project_name}</p>
                      <p className="text-xs text-zinc-500">{p.client_name}</p>
                    </div>
                    <span className="text-xs text-zinc-400 font-mono">
                      {p.delivery_date ? new Date(p.delivery_date).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 py-4 text-center">Aucune livraison prevue</p>
            )}
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wider text-zinc-500 font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" /> Activité récente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_activity?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-800/50 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-300">{a.action}</p>
                      <p className="text-xs text-zinc-500">{a.entity_name} - {a.user_name}</p>
                    </div>
                    <span className="text-xs text-zinc-600 flex-shrink-0">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR') : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 py-4 text-center">Aucune activite recente</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">À recevoir</p>
          <p className="text-xl font-bold text-sky-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.total_payments_due)}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">Encaissé</p>
          <p className="text-xl font-bold text-emerald-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.total_payments_received)}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-1">Total projets</p>
          <p className="text-xl font-bold text-zinc-100" style={{fontFamily:'Cabinet Grotesk'}}>{stats?.total_projects || 0}</p>
        </div>
      </div>
    </div>
  );
}
